import { cn } from '../../lib/cn'
import { ClubMark } from './ClubMark'
import type { ClubMark as Mark } from '../../lib/types'

type AvatarSize = 'sm' | 'md' | 'lg'

interface MemberAvatarProps {
  /** The member's display name or email; only its first letter is drawn. */
  name?: string | null
  /** The member's chosen mark. 'initial' (the default) keeps the monogram. */
  mark?: Mark
  size?: AvatarSize
  className?: string
}

// sm 28 — tables · md 34 — nav · lg 56 — account header (components card §VI).
const sizeStyles: Record<AvatarSize, string> = {
  sm: 'size-7 text-xs',
  md: 'size-[34px] text-sm',
  lg: 'size-14 text-[22px]',
}

/**
 * Sage ground, brass ring, a serif initial — the member reduced to a monogram.
 *
 * Decorative: the member's name is always rendered as text beside it, so the
 * initial is hidden from assistive tech rather than read out twice.
 */
export function MemberAvatar({ name, mark = 'initial', size = 'md', className }: MemberAvatarProps) {
  const initial = name?.trim()?.charAt(0)?.toUpperCase() || '·'

  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid shrink-0 place-items-center rounded-full border border-brass bg-sage-soft',
        'font-display font-semibold text-fairway',
        sizeStyles[size],
        className,
      )}
    >
      {mark === 'initial' ? initial : <ClubMark mark={mark} className="size-[62%]" />}
    </span>
  )
}
